import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { Navigate } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import RateCard from "@/pages/RateCard";
import RateAnalysis from "@/pages/RateAnalysis";
import SaleLogs from "@/pages/SaleLogs";
import PurchaseLogs from "@/pages/PurchaseLogs";
import SaleCalculator from "@/pages/SaleCalculator";
import PublicOrder from "@/pages/PublicOrder";
import EmployeeFoodLedger from "@/pages/EmployeeFoodLedger";
import PurchaseEntry from "@/pages/PurchaseEntry";
import SaleEntry from "@/pages/SaleEntry";
import TransferEntry from "@/pages/TransferEntry";
import AdjustmentEntry from "@/pages/AdjustmentEntry";
import BulkPurchaseEntry from "@/pages/BulkPurchaseEntry";
import RefillEntry from "@/pages/RefillEntry";
import PaymentEntry from "@/pages/PaymentEntry";
import Parties from "@/pages/Parties";
import PartyLedger from "@/pages/PartyLedger";
import PendingPayments from "@/pages/PendingPayments";
import PendingBillEntry from "@/pages/PendingBillEntry";
import RegisterEntry from "@/pages/RegisterEntry";
import Sales from "@/pages/Sales";
import PaymentFollowups from "@/pages/PaymentFollowups";
import OrderPredictions from "@/pages/OrderPredictions";
import PerformanceDashboard from "@/pages/PerformanceDashboard";
import PaymentsLog from "@/pages/PaymentsLog";
import StockMovement from "@/pages/StockMovement";
import AdjustmentLog from "@/pages/AdjustmentLog";
import StockView from "@/pages/StockView";
import CalendarView from "@/pages/CalendarView";
import Diesel from "@/pages/Diesel";
import Checklist from "@/pages/Checklist";
import Chat from "@/pages/Chat";
import PendingCylinders from "@/pages/PendingCylinders";
import Challans from "@/pages/Challans";
import ChallanGaps from "@/pages/ChallanGaps";
import DailyClosing from "@/pages/DailyClosing";
import Masters from "@/pages/Masters";
import AuditLog from "@/pages/AuditLog";
import Expenses from "@/pages/Expenses";
import PendingMT from "@/pages/PendingMT";
import Tally from "@/pages/Tally";
import Downloads from "@/pages/Downloads";
import Employees from "@/pages/Employees";
import EmployeeLedger from "@/pages/EmployeeLedger";
import Attendance from "@/pages/Attendance";
import AttendanceSummary from "@/pages/AttendanceSummary";
import Vehicles from "@/pages/Vehicles";
import VehicleStats from "@/pages/VehicleStats";
import IndustrialItems from "@/pages/IndustrialItems";
import IndustrialPurchase from "@/pages/IndustrialPurchase";
import IndustrialSale from "@/pages/IndustrialSale";
import IndustrialStock from "@/pages/IndustrialStock";
import DataTransfer from "@/pages/DataTransfer";
import { ConnectionBanner } from "@/components/ConnectionBanner";

function DashboardOrStockView() {
  const { user } = useAuth();
  if (user?.role === "stock_viewer") return <Navigate to="/stock-view" replace />;
  if (user?.role === "sales") return <Navigate to="/sales" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <ConnectionBanner />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/order" element={<PublicOrder />} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<DashboardOrStockView />} />
              <Route path="/rate-card" element={<RateCard />} />
              <Route path="/rate-analysis" element={<RateAnalysis />} />
              <Route path="/logs/sales" element={<SaleLogs />} />
              <Route path="/logs/purchases" element={<PurchaseLogs />} />
              <Route path="/sale-calculator" element={<SaleCalculator />} />
              <Route path="/entry/purchase" element={<PurchaseEntry />} />
              <Route path="/entry/sale" element={<SaleEntry />} />
              <Route path="/entry/transfer" element={<TransferEntry />} />
              <Route path="/entry/adjustment" element={<AdjustmentEntry />} />
              <Route path="/entry/bulk-purchase" element={<BulkPurchaseEntry />} />
              <Route path="/entry/refill" element={<RefillEntry />} />
              <Route path="/entry/payment" element={<PaymentEntry />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/parties" element={<Parties />} />
              <Route path="/parties/:id" element={<PartyLedger />} />
              <Route path="/pending/payments" element={<PendingPayments />} />
              <Route path="/pending/bills" element={<PendingBillEntry />} />
              <Route path="/register" element={<RegisterEntry />} />
              <Route path="/sales" element={<Sales />} />
              <Route path="/followups" element={<PaymentFollowups />} />
              <Route path="/predictions" element={<OrderPredictions />} />
              <Route path="/performance" element={<PerformanceDashboard />} />
              <Route path="/payments-log" element={<PaymentsLog />} />
              <Route path="/stock-movement" element={<StockMovement />} />
              <Route path="/adjustments/log" element={<AdjustmentLog />} />
              <Route path="/stock-view" element={<StockView />} />
              <Route path="/calendar" element={<CalendarView />} />
              <Route path="/diesel" element={<Diesel />} />
              <Route path="/checklist" element={<Checklist />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/pending/mt" element={<PendingMT />} />
              <Route path="/pending/cylinders" element={<PendingCylinders />} />
              <Route path="/challans" element={<Challans />} />
              <Route path="/challans/gaps" element={<ChallanGaps />} />
              <Route path="/reports/daily-closing" element={<DailyClosing />} />
              <Route path="/tally" element={<Tally />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/attendance/summary" element={<AttendanceSummary />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/employees/:id/ledger" element={<EmployeeLedger />} />
              <Route path="/employees/:id/food-ledger" element={<EmployeeFoodLedger />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/vehicles/:id" element={<VehicleStats />} />
              <Route path="/downloads" element={<Downloads />} />
              <Route path="/masters" element={<Masters />} />
              <Route path="/industrial/items" element={<IndustrialItems />} />
              <Route path="/industrial/purchase" element={<IndustrialPurchase />} />
              <Route path="/industrial/sale" element={<IndustrialSale />} />
              <Route path="/industrial/stock" element={<IndustrialStock />} />
              <Route path="/audit" element={<ProtectedRoute adminOnly><AuditLog /></ProtectedRoute>} />
              <Route path="/data-transfer" element={<ProtectedRoute adminOnly><DataTransfer /></ProtectedRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
